/** Bounds belong to the private hand-in journey, not the application's global transport. */
export const PRIVATE_UPLOAD_WAIT_MS = 120_000;
export const PRIVATE_UPLOAD_MAX_MS = 15 * 60_000;
export const PRIVATE_REQUEST_WAIT_MS = 20_000;
export const PRIVATE_FILE_CHECK_WAIT_MS = 8_000;

export class PrivateRequestUnconfirmed extends Error {
  constructor() {
    super("That request was not confirmed. Your entry is kept. Check its saved state before trying again.");
  }
}

/** A stalled native request becomes uncertain even if its adapter ignores abort. */
export function privateRequest<T>(run: (signal: AbortSignal) => Promise<T>, waitMs = PRIVATE_REQUEST_WAIT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;
    const finish = (answer: () => void) => {
      if (settled) return;
      settled = true; clearTimeout(timer); answer();
    };
    const timer = setTimeout(() => {
      finish(() => reject(new PrivateRequestUnconfirmed()));
      controller.abort();
    }, waitMs);
    try { run(controller.signal).then(value => finish(() => resolve(value)), error => finish(() => reject(error))); }
    catch (error) { finish(() => reject(error)); }
  });
}

export type FileTransfer = { state: "transferred" } | { state: "uncertain"; reason: "cancelled" | "stalled" | "network" | "response" };
export type FileProgress = { loaded: number; total: number };

/** Only byte-transfer credentials go to storage. Every uncertain result requires
 * an IMAM arrival read before another PUT. A successful PUT is not a hand-in. */
export function putPrivateFile(url: string, file: File, contentType: string, options: {
  signal: AbortSignal; progress: (value: FileProgress) => void;
}): Promise<FileTransfer> {
  return new Promise(resolve => {
    if (options.signal.aborted) { resolve({ state: "uncertain", reason: "cancelled" }); return; }
    const request = new XMLHttpRequest();
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let loaded = 0;
    const finish = (answer: FileTransfer) => {
      if (settled) return;
      settled = true; clearTimeout(timer); clearTimeout(maximum);
      options.signal.removeEventListener("abort", cancel);
      request.onload = request.onerror = request.onabort = null;
      request.upload.onprogress = null;
      resolve(answer);
    };
    const cancel = () => { finish({ state: "uncertain", reason: "cancelled" }); request.abort(); };
    options.signal.addEventListener("abort", cancel, { once: true });
    request.onload = () => finish(request.status >= 200 && request.status < 300
      ? { state: "transferred" } : { state: "uncertain", reason: "response" });
    request.onerror = () => finish({ state: "uncertain", reason: "network" });
    request.onabort = () => finish({ state: "uncertain", reason: "cancelled" });
    const stalled = () => { finish({ state: "uncertain", reason: "stalled" }); request.abort(); };
    request.upload.onprogress = event => {
      if (!settled && event.loaded > loaded) {
        loaded = event.loaded; clearTimeout(timer); timer = setTimeout(stalled, PRIVATE_UPLOAD_WAIT_MS);
      }
      if (!settled && event.lengthComputable && event.total > 0) {
        options.progress({ loaded: Math.min(event.loaded, event.total), total: event.total });
      }
    };
    timer = setTimeout(stalled, PRIVATE_UPLOAD_WAIT_MS);
    const maximum = setTimeout(stalled, PRIVATE_UPLOAD_MAX_MS);
    try {
      request.open("PUT", url, true);
      request.withCredentials = false;
      request.setRequestHeader("Content-Type", contentType);
      request.setRequestHeader("x-goog-if-generation-match", "0");
      request.send(file);
    } catch { finish({ state: "uncertain", reason: "network" }); }
  });
}
